import React, { useMemo } from 'react';
import { ColumnDef } from '@tanstack/react-table';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { EyeIcon } from '@phosphor-icons/react';
import { DataTable } from '@/components/common/data-table/data-table';
import { useDataTable } from '@/components/common/data-table/use-data-table';
import { ServerSidePagination } from './ServerSidePagination';
import { DataTableToolbar } from '@/components/common/data-table/data-table-toolbar';
import { DataTableFacetedFilter } from '@/components/common/data-table/data-table-faceted-filters';
import {
  RexelFileDiscoveryJob,
  RexelFileDiscoveryJobStatus,
  PaginatedResponse,
  DiscoveryJobsQuery,
} from '@/api/rexel/types';
import { formatDistanceToNow } from 'date-fns';

interface DiscoveryJobsTableProps {
  data: PaginatedResponse<RexelFileDiscoveryJob> | undefined;
  isLoading: boolean;
  filters: DiscoveryJobsQuery;
  onFiltersChange: (filters: Partial<DiscoveryJobsQuery>) => void;
}

const getStatusBadgeVariant = (status: RexelFileDiscoveryJobStatus) => {
  switch (status) {
    case RexelFileDiscoveryJobStatus.completed:
      return 'secondary';
    case RexelFileDiscoveryJobStatus.running:
      return 'success';
    case RexelFileDiscoveryJobStatus.failed:
      return 'destructive';
    case RexelFileDiscoveryJobStatus.pending:
      return 'outline';
    default:
      return 'outline';
  }
};

const formatDuration = (
  startedAt: string | null,
  completedAt: string | null,
): string => {
  if (!startedAt || !completedAt) return '-';
  const duration =
    (new Date(completedAt).getTime() - new Date(startedAt).getTime()) / 1000;
  if (duration < 60) return `${Math.round(duration)}s`;
  if (duration < 3600) return `${Math.round(duration / 60)}m`;
  return `${Math.round(duration / 3600)}h`;
};

const DiscoveryJobsTable: React.FC<DiscoveryJobsTableProps> = ({
  data,
  isLoading,
  filters,
  onFiltersChange,
}) => {
  const columns: ColumnDef<RexelFileDiscoveryJob>[] = useMemo(
    () => [
      {
        accessorKey: 'containerName',
        header: 'Container',
        cell: ({ row }) => (
          <div className="font-medium">{row.getValue('containerName')}</div>
        ),
      },
      {
        accessorKey: 'status',
        header: 'Status',
        cell: ({ row }) => {
          const status = row.getValue('status') as RexelFileDiscoveryJobStatus;
          return (
            <Badge variant={getStatusBadgeVariant(status)}>{status}</Badge>
          );
        },
      },
      {
        accessorKey: 'filesDiscovered',
        header: 'Files Discovered',
        cell: ({ row }) => {
          const discovered = row.getValue('filesDiscovered') as number | null;
          return discovered !== null ? discovered.toLocaleString() : '-';
        },
      },
      {
        accessorKey: 'filesQueued',
        header: 'Files Queued',
        cell: ({ row }) => {
          const queued = row.getValue('filesQueued') as number | null;
          return queued !== null ? queued.toLocaleString() : '-';
        },
      },
      {
        accessorKey: 'lastCheckedTimestamp',
        header: 'Last Checked',
        cell: ({ row }) => {
          const timestamp = row.getValue('lastCheckedTimestamp') as
            | string
            | null;
          if (!timestamp) return '-';

          // Parse the timestamp format (e.g., "20241201120000")
          const year = timestamp.substring(0, 4);
          const month = timestamp.substring(4, 6);
          const day = timestamp.substring(6, 8);
          const hour = timestamp.substring(8, 10);
          const minute = timestamp.substring(10, 12);
          const second = timestamp.substring(12, 14);

          const date = new Date(
            `${year}-${month}-${day}T${hour}:${minute}:${second}`,
          );

          return (
            <div className="text-sm">
              <div>{formatDistanceToNow(date, { addSuffix: true })}</div>
              <div className="text-muted-foreground text-xs">
                {date.toLocaleString()}
              </div>
            </div>
          );
        },
      },
      {
        accessorKey: 'createdAt',
        header: 'Created',
        cell: ({ row }) => {
          const date = new Date(row.getValue('createdAt') as string);
          return (
            <div className="text-sm">
              <div>{formatDistanceToNow(date, { addSuffix: true })}</div>
              <div className="text-muted-foreground text-xs">
                {date.toLocaleString()}
              </div>
            </div>
          );
        },
      },
      {
        header: 'Duration',
        cell: ({ row }) => {
          return formatDuration(
            row.original.startedAt,
            row.original.completedAt,
          );
        },
      },
      {
        id: 'actions',
        cell: ({ row }) => {
          const job = row.original;

          return (
            <div className="flex items-center gap-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  // TODO: Open job details modal
                  console.log('View discovery job details:', job.id);
                }}
              >
                <EyeIcon className="h-4 w-4" />
              </Button>
            </div>
          );
        },
      },
    ],
    [],
  );

  const statusOptions = [
    { label: 'Completed', value: RexelFileDiscoveryJobStatus.completed },
    { label: 'Running', value: RexelFileDiscoveryJobStatus.running },
    { label: 'Failed', value: RexelFileDiscoveryJobStatus.failed },
    { label: 'Pending', value: RexelFileDiscoveryJobStatus.pending },
  ];

  const table = useDataTable({
    columns,
    data: data?.data || [],
    options: {
      initialState: {
        pagination: { pageSize: filters.limit || 25 },
      },
    },
  });

  return (
    <div className="space-y-4">
      <DataTableToolbar
        table={table}
        searchPlaceholder="Search by container name..."
        searchValue={filters.containerName || ''}
        onSearchChange={(value) => onFiltersChange({ containerName: value })}
        facetedFilters={
          <DataTableFacetedFilter
            column={table.getColumn('status')}
            title="Status"
            options={statusOptions}
          />
        }
      />

      <DataTable
        table={table}
        isLoading={isLoading}
        emptyMessage="No discovery jobs found"
      />

      {data && (
        <ServerSidePagination
          pagination={data.pagination}
          onPageChange={(page) => onFiltersChange({ page })}
          onPageSizeChange={(limit) => onFiltersChange({ limit })}
        />
      )}
    </div>
  );
};

export default DiscoveryJobsTable;
